import { computed, ref } from '@vue/composition-api'
import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

export default function useAreaList() {
  // Use toast
  const toast = useToast()
  const refReportListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'created',
      label: i18n.t('reports.created_at'),
    },
    {
      key: 'areaName',
      label: i18n.t('reports.area'),
    },
    {
      key: 'totalShifts',
      label: i18n.t('reports.total_shifts'),
    },
    {
      key: 'status',
      label: i18n.t('reports.status'),
    },
    {
      key: 'start',
      label: i18n.t('reports.start'),
    },
    {
      key: 'end',
      label: i18n.t('reports.end'),
    },
    {
      key: 'createdBy',
      label: i18n.t('reports.created_by'),
    },
    {
      key: 'actions',
      label: i18n.t('actions'),
    },
  ]
  let deleteId = null

  const selectedAccount = computed(() => store.state.appAccount.selectedAccount)
  const accountId = selectedAccount.value.value.id

  const refetchData = () => {
    refReportListTable.value.refresh()
  }

  function fetchReports(ctx, callback) {
    store.dispatch('appReport/fetchReports', {
      accountId,
    })
      .then(data => {
        const results = data.map(item => {
          const doc = item
          return doc
        })
        callback(results)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching reports list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  async function createReports({
    reports,
    start,
    end,
    status,
  }) {
    const startTimeObject = new Date(start)
    const startDate = startTimeObject.getDate()
    const startMonth = startTimeObject.getMonth() + 1
    const startYear = startTimeObject.getFullYear()
    const startStr = `${startDate}-${startMonth}-${startYear}`

    const endTimeObject = new Date(end)
    const endDate = endTimeObject.getDate()
    const endMonth = endTimeObject.getMonth() + 1
    const endYear = endTimeObject.getFullYear()
    const endStr = `${endDate}-${endMonth}-${endYear}`
    const fields = [
      'First Name', 'Last Name', 'Start', 'End',
    ]
    await Promise.all(reports.map(async report => {
      const cloneReport = { ...report }
      cloneReport.shifts = await Promise.all(report.shifts.map(async shift => {
        const { firstName, lastName } = shift.assignedTo
          ? await store.dispatch('appReport/fetchUserDetails', { assignedTo: shift.assignedTo })
          : {
            firstName: '',
            lastName: '',
          }
        const assignedTo = { id: shift.assignedTo, firstName, lastName }
        return { ...shift, assignedTo }
      }))

      const rpShifts = cloneReport.shifts
      const rpLocation = cloneReport.location
      const locationStr = `${rpLocation.address.streetAddress}, ${rpLocation.address.postcode} ${rpLocation.address.city}`

      const rows = await Promise.all(rpShifts.map(shift => {
        const row = {
          'First Name': shift.assignedTo.firstName,
          'Last Name': shift.assignedTo.lastName,
          Location: locationStr,
          Start: new Date(shift.startStr).toLocaleString('fi'),
          End: new Date(shift.endStr).toLocaleString('fi'),
          Status: status,
        }
        return row
      }))

      await store.dispatch('appReport/createReport', {
        name: `${report.name}_${status}_${startStr}_${endStr}`,
        areaId: report.id,
        areaName: report.name,
        totalShifts: report.total_shifts,
        start,
        end,
        status,
        accountId,
        rpHeaders: fields,
        rpBody: rows,
        createdBy: cloneReport.createdBy,
      }).then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Report saved',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        refetchData()
      })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error saving report list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }))
  }

  function fetchAreasForReport({
    start, end, status, callback,
  }) {
    store.dispatch('appReport/fetchAreasForReport', {
      start,
      end,
      accountId,
      status,
    }).then(data => {
      callback(data)
    })
  }

  const onDeleteReport = id => {
    deleteId = id
  }

  function confirmDelete() {
    store.dispatch('appReport/deleteReport', { id: deleteId })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Report deleted',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        refetchData()
      }).catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error deleting report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  return {
    createReports,
    fetchAreasForReport,
    tableColumns,
    refReportListTable,
    fetchReports,
    confirmDelete,
    onDeleteReport,
  }
}
